import { useEffect, useRef } from 'react';
import { useUIState } from '../contexts/UIStateContext';
import { useProject } from '../contexts/ProjectContext';
import { buildAppPath, parseAppPath, isAppPath } from '../lib/appRoute';

/**
 * Mantém a barra de endereço e o estado de navegação em acordo.
 *
 * Duas direções, uma regra de desempate:
 *
 *   estado -> URL   ao trocar de módulo ou de projeto, empilha (pushState) para
 *                   o botão voltar ter para onde voltar.
 *   URL -> estado   no popstate, reidrata módulo e projeto a partir do endereço.
 *
 * Não há flag de "estou voltando" e isso é proposital. O popstate dispara DEPOIS
 * que o endereço já mudou, então quando o estado termina de acompanhar, o efeito
 * de escrita encontra o alvo igual ao que já está na barra e não empilha nada.
 * Uma flag daria o mesmo resultado no caminho feliz e ficaria presa em `true`
 * quando o popstate apontasse para o módulo que já estava aberto, engolindo a
 * navegação seguinte.
 *
 * Normalização usa replaceState, não pushState: chegar em "/" e cair no Gantt
 * não é navegação do usuário, e virar entrada de histórico faria o primeiro
 * "voltar" não sair do lugar.
 */
export function useAppRouteSync(): void {
  const { currentModule, changeModule } = useUIState();
  const { currentProjectId, switchProject, projects, loading } = useProject();

  // Estado -> URL.
  useEffect(() => {
    const naUrl = parseAppPath(window.location.pathname);

    // O endereço manda enquanto o projeto dele não foi adotado.
    //
    // No primeiro quadro de um link profundo o `currentProjectId` ainda é nulo
    // (a lista de projetos vive na rede), e `buildAppPath` sem projeto devolve
    // "/kanban". Sem esta guarda, este efeito reescrevia /p/<id>/kanban para
    // /kanban e apagava da barra justamente o id que o `ProjectContext` lê
    // depois para escolher o projeto: o link caía no projeto padrão da conta e
    // a tarefa nunca abria.
    //
    // Conta sem projeto nenhum continua normalizando: quando `loading` vira
    // false sem projeto, o id do endereço não vai virar tela nunca, e manter a
    // barra apontando para ele seria mentira permanente.
    if (naUrl.projectId && !currentProjectId && loading) return;

    const target = buildAppPath({ projectId: currentProjectId, module: currentModule });
    if (target === window.location.pathname) return;

    // Query string sobrevive de propósito: ?checkout=success e ?upgrade=1 chegam
    // por e-mail e por retorno do Stripe, e quem limpa cada um é o efeito dono
    // do fluxo, no App.tsx.
    const url = target + window.location.search;

    // Endereço com projeto que não é da conta (link de outra pessoa, projeto
    // excluído) é conserto, não navegação: com pushState, o "voltar" devolvia o
    // usuário ao endereço quebrado, que se corrigia de novo, e o botão virava
    // um laço.
    const ehCorrecao = naUrl.projectId !== null && !projects.some(p => p.id === naUrl.projectId);

    if (isAppPath(window.location.pathname) && !ehCorrecao) {
      window.history.pushState({}, '', url);
    } else {
      window.history.replaceState({}, '', url);
    }
  }, [currentModule, currentProjectId, loading, projects]);

  // URL -> estado. O handler fica em ref para o listener ser registrado uma
  // única vez: `switchProject` nasce de novo a cada render do ProjectContext, e
  // como dependência faria assinar e desassinar sem parar.
  const handlePopRef = useRef<() => void>(() => {});
  handlePopRef.current = () => {
    const { projectId, module } = parseAppPath(window.location.pathname);
    if (!module) return;

    // Projeto de entrada antiga do histórico (ou link de fora) pode não existir
    // mais, ou nunca ter sido do usuário. Ignorar o id e ficar no projeto atual
    // é melhor do que mandar o ProjectContext buscar algo que devolve erro de
    // tela cheia.
    if (projectId && projectId !== currentProjectId && projects.some(p => p.id === projectId)) {
      switchProject(projectId);
    }
    if (module !== currentModule) {
      changeModule(module);
    }
  };

  useEffect(() => {
    const onPopState = () => handlePopRef.current();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
}
